import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import dayjs from 'dayjs';

import { TripTypeEnum } from '@mopla/data-models';
import { getDurationStr } from '@mopla/utils';

import { ChevronRightIcon } from '../../../icons/ChevronRight';
import { IVehicleIconProps } from '../../../icons/Vehicles/types';
import { VehicleIcon } from '../../../icons/Vehicles/VehicleIcon';
import walkIcon from '../../../icons/walk.svg?url';
import { ChevronWrapper, LabelWrapper } from '../RideHailingService.styles';
import { IRideHailingServiceLabel } from '../utils/deriveRideHailingLabels';

import { RideHailingServiceChips } from './RideHailingServiceChips';

interface IRideHailingLabelProps {
	labels: IRideHailingServiceLabel[];
	testId: string;
}
/** Compact chain of transport chips, one per label, in travel order. */
export const RideHailingLabel: FC<IRideHailingLabelProps> = ({
	labels,
	testId,
}) => {
	const { t } = useTranslation(['common']);

	if (!labels.length) return null;

	return (
		<LabelWrapper>
			{labels.map((label, index) => {
				const isLast = index === labels.length - 1;

				const labelText =
					label.durationMin != null
						? getDurationStr(t, dayjs.duration(label.durationMin, 'minutes'), {
								exclude: ['seconds'],
						  })
						: label.route ?? t(`ride_types.${label.mode}.title`);

				const vehicleIconProps: IVehicleIconProps | null =
					label.durationMin != null
						? null
						: {
								mode: label.mode as Exclude<
									IVehicleIconProps['mode'],
									TripTypeEnum.PRODT | TripTypeEnum.LBODTFLEX
								>,
						  };

				const icon =
					label.durationMin != null || !vehicleIconProps ? (
						<img src={walkIcon} width={16} height={16} alt={t('text.toWalk')} />
					) : (
						<VehicleIcon {...vehicleIconProps} size="xs" />
					);

				return (
					<Box
						key={`label-${index}`}
						sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
					>
						<RideHailingServiceChips
							label={labelText}
							icon={icon}
							isWalkChip={label.durationMin != null}
							testId={`${testId}-chip-${index}`}
						/>
						{!isLast && (
							<ChevronWrapper>
								<ChevronRightIcon />
							</ChevronWrapper>
						)}
					</Box>
				);
			})}
		</LabelWrapper>
	);
};
